import { Button, Checkbox, Intent } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { ButtonContainer, FieldSet, IDataTableColumn, showSuccess, useGridState, useSearchApi } from "nsitools-react";
import * as React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { DogApi, DogGridDto, DogSearch } from "../../../api";
import { CustomDataTable, ExportButton } from "../../../components";

import { useAuth, useDialog } from "../../../contexts";
import { ERoutes } from "../../../ERoutes";
import { useApiService, useTl } from "../../../hooks";
import { ETLCodes } from "../../../locales";

export interface IDogAwaitingUpdateTaskListProps {
  isAssigned: boolean;
  sportSection: boolean;
}

const StyledCheckbox = styled(Checkbox)`
  font-size: 10pt;
  top: 6px;
`;

export const DogAwaitingUpdateTaskList: React.FunctionComponent<IDogAwaitingUpdateTaskListProps> = ({
  isAssigned,
  sportSection,
}) => {
  const api = useApiService(DogApi);
  const { t, tUnsafe } = useTl();
  const { showDialog } = useDialog();
  const [hideClosed, sethideClosed] = React.useState(true);
  const [sObjState, setsObjState] = React.useState<any>();
  const selectionList = React.useMemo(() => [], []);
  const editableIdsList = React.useMemo(() => [], []);
  const [selectAll, setselectAll] = React.useState(false);
  const [Actionloading, setActionloading] = React.useState(false);
  const { user } = useAuth();

  const tableState = useGridState<any>({
    serverMode: true,
    availablePageSizes: [10],
    enablePagination: true,
    enableFilter: true,
    sortKeys: { computedName: "ASC" },
  });

  const searchFunction = React.useCallback(
    (sObj?: DogSearch) => {
      setsObjState(sObj);
      return api.searchDogTransient(hideClosed, isAssigned ? user.userId : null, sportSection, sObj);
    },
    [api, hideClosed, isAssigned, sportSection, user.userId]
  );

  const { search, loading } = useSearchApi<any, any>({
    searchFunction,
    tableState,
    initialSearch: true,
    trimSearchObjectStringValues: true,
  });

  const toggleSelectAll = React.useCallback(() => {
    const value = !selectAll;
    if (value) {
      selectionList.splice(0, selectionList.length, ...editableIdsList);
    } else {
      selectionList.length = 0;
    }
    setselectAll(value);
  }, [editableIdsList, selectAll, selectionList]);

  const columnCheckBox = React.useCallback(
    (row: DogGridDto) => {
      if (!row.closedOn) {
        editableIdsList.push(row.dogTransientId);
      }

      return (
        <Checkbox
          checked={selectionList.find((c) => c === row.dogId) && !row.closedOn}
          onChange={() => {
            if (selectionList.includes(row.dogId)) {
              const index = selectionList.findIndex((e) => e === row.dogId);
              selectionList.splice(index, 1);
            } else {
              selectionList.push(row.dogId);
            }
          }}
          disabled={!!row.closedOn}
        />
      );
    },
    [editableIdsList, selectionList]
  );

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        header: () => <Checkbox onChange={toggleSelectAll} />,
        computed: true,
        disableSorting: true,
        fieldName: "selector",
        autoFitContent: true,
        render: columnCheckBox,
      },
      {
        header: () => t(ETLCodes.CompleteName),
        fieldName: "computedName",
        render: (row: DogGridDto) => (
          <Link target="_blank" to={`${ERoutes.dog}/${row.dogId}/detail/view`}>
            {row.computedName}
          </Link>
        ),
      },
      {
        header: () => t(ETLCodes.Breed),
        fieldName: "breedName",
        render: (row: DogGridDto) => (
          <Link target="_blank" to={`${ERoutes.breeds}/${row.breedId}/detail/view`}>
            {row.breedName}
          </Link>
        ),
      },
      {
        header: () => t(ETLCodes.Color),
        fieldName: "color",
        render: (row: DogGridDto) => (
          <Link target="_blank" to={`${ERoutes.color}/${row.colorId}/detail/view`}>
            {row.color}
          </Link>
        ),
      },
      {
        header: () => t(ETLCodes.Sex),
        fieldName: "sex",
        render: (row: DogGridDto) => <div>{tUnsafe(row.sex)}</div>,
      },
      {
        header: () => t(ETLCodes.Identification1),
        computed: true,
        disableSorting: true,
        fieldName: "dogChip",
      },
      {
        header: () => t(ETLCodes.Identification2),
        computed: true,
        disableSorting: true,
        fieldName: "secondDogChip",
      },
      {
        header: () => t(ETLCodes.Type),
        fieldName: "type",
      },
    ],
    [columnCheckBox, t, tUnsafe, toggleSelectAll]
  );

  const action = React.useCallback(
    async (requestList: number[]) => {
      setActionloading(true);
      if (requestList.length === 0) {
        showDialog({
          message: t(ETLCodes.SelectionEmpty),
          title: t(ETLCodes.SelectionEmpty),
          buttons: [
            {
              resultType: "yes",
              text: t(ETLCodes.General_Ok),
              intent: Intent.PRIMARY,
            },
          ],
          onConfirmed: async () => {},
        });
      } else {
        if (!isAssigned) {
          showDialog({
            message: t(ETLCodes.AssignElementsQuestion),
            title: t(ETLCodes.Assign),
            onConfirmed: async () => {
              await api.assignManager(user.userId, requestList);
              search();
            },
          });
        } else {
          showDialog({
            message: t(ETLCodes.ValidateElementsQuestion),
            title: t(ETLCodes.Validate),
            onConfirmed: async () => {
              await api.validateDog(requestList);
              search();
            },
          });
        }
      }
      setActionloading(false);
    },
    [api, isAssigned, search, showDialog, t, user.userId]
  );

  const refuse = React.useCallback(
    (requestList: number[]) => {
      setActionloading(true);
      if (requestList.length === 0) {
        showDialog({
          message: t(ETLCodes.SelectionEmpty),
          title: t(ETLCodes.SelectionEmpty),
          buttons: [
            {
              resultType: "yes",
              text: t(ETLCodes.General_Ok),
              intent: Intent.PRIMARY,
            },
          ],
          onConfirmed: async () => {},
        });
      } else {
        showDialog({
          message: t(ETLCodes.CloseTaskConfirmation),
          title: t(ETLCodes.Close),
          onConfirmed: async () => {
            await api.dogTransientDiscardTask(requestList);
            showSuccess(t(ETLCodes.SaveSuccess));
            search();
          },
        });
        setActionloading(false);
      }
    },
    [api, search, showDialog, t]
  );

  return (
    <FieldSet
      title={!isAssigned ? t(ETLCodes.DogToAssign) : t(ETLCodes.DogToValidate)}
      rightElement={
        <ButtonContainer>
          <Button
            text={!isAssigned ? t(ETLCodes.Assign) : t(ETLCodes.Validate)}
            onClick={() => action(selectionList)}
            intent={Intent.PRIMARY}
            minimal={true}
            icon={IconNames.TICK_CIRCLE}
            loading={Actionloading}
          />
          {!!isAssigned && (
            <Button
              text={t(ETLCodes.Refuse)}
              onClick={() => refuse(selectionList)}
              intent={Intent.PRIMARY}
              minimal={true}
              icon={IconNames.DELETE}
              loading={Actionloading}
            />
          )}
          {!!isAssigned && (
            <StyledCheckbox
              checked={hideClosed}
              onClick={() => {
                sethideClosed(!hideClosed);
                search();
              }}
              label={t(ETLCodes.HideClosed)}
            />
          )}
          <ExportButton exportFunction={(sObj) => api.exportXlsFile(sObj)} sObj={sObjState} />
        </ButtonContainer>
      }
    >
      <CustomDataTable tableState={tableState} loading={loading} columns={columns} />
    </FieldSet>
  );
};
